import { HelpCircle, MessageSquare } from 'lucide-react';
import type { GbpQuestionRowWire } from '@/lib/types';
import { cn } from '@/lib/utils';
import { QuestionAnswerBox } from './question-answer-box';

// Real gbp_questions rows (S4) — the stub GbpQuestionView era is over. Each
// row shows the question, then either the owner's posted answer, an
// in-flight/failed state, or the answer box. A 'failed' answer's preserved
// text prefills the box (no owner text lost, on the wire and on screen).

interface GbpQuestionsListProps {
  questions: GbpQuestionRowWire[];
  siteId: string;
  canWrite: boolean;
  onChanged: (announcement?: string) => void;
}

export function GbpQuestionsList({ questions, siteId, canWrite, onChanged }: GbpQuestionsListProps) {
  if (questions.length === 0) {
    return <p className="text-sm text-text-tertiary">No questions from customers yet.</p>;
  }
  return (
    <ul className="flex flex-col gap-4">
      {questions.map((q) => (
        <li key={q.id}>
          <QuestionRow question={q} siteId={siteId} canWrite={canWrite} onChanged={onChanged} />
        </li>
      ))}
    </ul>
  );
}

function QuestionRow({
  question,
  siteId,
  canWrite,
  onChanged,
}: {
  question: GbpQuestionRowWire;
  siteId: string;
  canWrite: boolean;
  onChanged: (announcement?: string) => void;
}) {
  const answer = question.answer;
  const meta = questionMetaLine(question);
  return (
    <article className="flex gap-3">
      <div className="mt-0.5 shrink-0">
        <HelpCircle className="h-4 w-4 text-text-tertiary" aria-hidden />
      </div>
      <div className="min-w-0 flex-1">
        <header className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-foreground">
            {question.askerName ?? 'A Google user'}
          </span>
          <span className="text-xs text-text-tertiary">{formatDate(question.postedAt)}</span>
        </header>
        <p className="mt-1 text-sm leading-relaxed text-foreground">{question.text}</p>
        {meta ? <p className="mt-1 text-xs text-text-tertiary">{meta}</p> : null}

        {answer?.status === 'posted' ? (
          <div className={cn('mt-2 rounded-md px-3 py-2', 'bg-brand-50')}>
            <div className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-700">
              <MessageSquare className="h-3 w-3" aria-hidden />
              Your answer
              {answer.answeredAt ? ` · ${formatDate(answer.answeredAt)}` : ''}
            </div>
            <p className="mt-1 text-sm leading-relaxed text-text-secondary">{answer.text}</p>
          </div>
        ) : answer?.status === 'sending' ? (
          // Claimed by an in-flight post — nothing for the owner to do this
          // second (same voice as posts-list's 'sending' row).
          <p className="mt-2 text-xs text-text-tertiary">Posting your answer to Google…</p>
        ) : (
          <>
            {answer?.status === 'failed' ? (
              <p className="mt-2 text-sm text-danger-fg">
                Your last answer didn't reach Google — it's saved below, edit it and try again.
              </p>
            ) : null}
            <QuestionAnswerBox
              siteId={siteId}
              questionId={question.id}
              initialText={answer?.status === 'failed' ? answer.text : null}
              canWrite={canWrite}
              onChanged={onChanged}
            />
          </>
        )}
      </div>
    </article>
  );
}

// Addendum §A: upvoteCount/totalAnswerCount are `number | null` — null is
// "Google didn't report this," a fact we never fabricate as 0. Render only
// whichever of the two is actually present, joined with the same `·`
// idiom connected-panels.tsx uses for the reviews header (count + rating).
function questionMetaLine(question: GbpQuestionRowWire): string | null {
  const parts: string[] = [];
  if (question.upvoteCount !== null) {
    parts.push(`${question.upvoteCount} upvote${question.upvoteCount === 1 ? '' : 's'}`);
  }
  if (question.totalAnswerCount !== null) {
    parts.push(
      `${question.totalAnswerCount} answer${question.totalAnswerCount === 1 ? '' : 's'} on Google`,
    );
  }
  return parts.length > 0 ? parts.join(' · ') : null;
}

function formatDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
